/**
 * Two liberos (VOL-163): a reception libero plays while the team receives, a
 * defense libero while it serves. The swap is derived from the serving side,
 * never stored as an event, so replay, undo and reload stay consistent. Any
 * `LIBERO_CHANGE` of that team in the set means the coach forced a libero: the
 * automatic swap stops until the next set. Same rule on the server replay.
 */
export interface LiberoPair {
  reception: string;
  defense: string;
}

export function isLiberoPair(value: unknown): value is LiberoPair {
  if (typeof value !== 'object' || value === null) return false;
  const reception = 'reception' in value ? value.reception : undefined;
  const defense = 'defense' in value ? value.defense : undefined;
  return (
    typeof reception === 'string' && reception !== '' &&
    typeof defense === 'string' && defense !== '' &&
    reception !== defense
  );
}

/** A pair only when both liberos are set and distinct; otherwise single-libero behaviour. */
export function toLiberoPair(reception: string | null | undefined, defense: string | null | undefined): LiberoPair | null {
  const pair = { reception, defense };
  return isLiberoPair(pair) ? pair : null;
}

/**
 * Pair declared on the match roster (`liberoRole`): the first set has no
 * `SET_START`, so this is where its pair lives. Same reading on the server.
 */
export function rosterLiberoPair(
  roster: readonly { name: string; liberoRole?: keyof LiberoPair }[],
): LiberoPair | null {
  return toLiberoPair(
    roster.find((p) => p.liberoRole === 'reception')?.name,
    roster.find((p) => p.liberoRole === 'defense')?.name,
  );
}

/** Liberos with the reception one first: it takes the libero slot of a lineup built from the roster. */
export function liberosReceptionFirst<T extends { name: string }>(liberos: T[], pair: LiberoPair | null): T[] {
  if (!pair) return liberos;
  return [...liberos].sort((a, b) => Number(b.name === pair.reception) - Number(a.name === pair.reception));
}

export function expectedLibero(pair: LiberoPair, teamServes: boolean): string {
  return teamServes ? pair.defense : pair.reception;
}

interface LiberoEventLike {
  set: number;
  action: string;
  isOpponent?: boolean;
}

export function liberoForcedInSet(
  events: readonly LiberoEventLike[],
  set: number,
  side: 'us' | 'them',
): boolean {
  return events.some(
    (e) => e.set === set && e.action === 'LIBERO_CHANGE' && (e.isOpponent ? 'them' : 'us') === side,
  );
}

interface LiberoTeamLike {
  court: string[];
  bench: string[];
  libero: string | null;
  liberos?: LiberoPair | null;
}

/**
 * Team fields to change for the automatic swap, or null when nothing moves: no
 * pair, libero forced or taken off, libero slot empty, or the right one already in.
 * Read on the libero slot (7th place), as the server replay does.
 */
export function autoLiberoSwap(
  team: LiberoTeamLike,
  teamServes: boolean,
  forced: boolean,
): Pick<LiberoTeamLike, 'court' | 'bench' | 'libero'> | null {
  const pair = team.liberos;
  const current = team.court[6];
  if (!pair || forced || !current) return null;
  if (current !== pair.reception && current !== pair.defense) return null;
  const wanted = expectedLibero(pair, teamServes);
  if (current === wanted) return null;
  const court = [...team.court];
  court[6] = wanted;
  const bench = [...team.bench.filter((p) => p !== wanted), current];
  return { court, bench, libero: wanted };
}
